import { useFlag } from '@automata/utils/hooks';
import Box from '@mui/material/Box';
import {
  DataGridPro,
  GridColDef,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';

import { Icon } from '../../../workflow-builder/src/components/customIcons/misc';
import { Modal } from '../Modal';

export type LabwarePositionInfo = {
  id: string;
  name: string;
  startLocation: {
    instrument: string;
    slot: number;
  };
  type: string;
}[];

const CustomToolbar = () => {
  return (
    <GridToolbarContainer
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
      }}
    >
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  );
};

const nameCol = { field: 'name', headerName: 'Labware name', flex: 1 };
const typeCol = { field: 'type', headerName: 'Labware type', flex: 2 };
const idCol = { field: 'id', headerName: 'Labware ID', flex: 2 };
const startLocationCol: GridColDef<LabwarePositionInfo[number]> = {
  field: 'startLocation',
  headerName: 'Start location [Slot #]',
  flex: 1,
  valueGetter: (_, row) =>
    `${row.startLocation.instrument} [${row.startLocation.slot}]`,
};

export const LabwarePositionModal = ({
  data,
  open,
  onClose,
}: {
  data: LabwarePositionInfo;
  open: boolean;
  onClose: () => void;
}) => {
  const isNewColumnsEnabled = useFlag('fe.run_instructions_columns');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'name',
      sort: 'asc',
    },
  ]);

  const columns = useMemo(() => {
    return isNewColumnsEnabled
      ? [nameCol, typeCol, startLocationCol]
      : [nameCol, idCol, startLocationCol];
  }, [isNewColumnsEnabled]);

  return (
    <Modal
      title="Labware"
      titleIcon={<Icon iconType="labware" />}
      open={open}
      onClose={onClose}
      maxWidth
      sx={{
        '& .MuiDialog-paper': {
          minHeight: '60vh',
        },
      }}
    >
      <Box
        sx={{
          height: 500,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGridPro
          sx={{
            flex: 1,
            overflow: 'auto',
            '& .MuiDataGrid-virtualScroller': {
              overflowY: 'auto',
            },
          }}
          hideFooterRowCount
          rows={data}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          columns={columns}
          slots={{
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 100 },
            },
          }}
          disableColumnMenu
        />
      </Box>
    </Modal>
  );
};
